/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/browser';
import $ from 'jquery';
import Mustache from 'mustache';

import { DEBUG_MODE, getBuildVersion } from '../spdConfig';

import { getActiveToken } from './auth';

export class LoaderError {
  constructor(
    public errorMessage: string,
    public statusCode: number,
    public message: string,
  ) {}
}

type LoaderErrorEntry = {
  error: LoaderError;
  count: number;
};

export class LoaderErrorsStack {
  private entries = new Map<string, LoaderErrorEntry>();

  private buildKey(error: LoaderError) {
    return [error.statusCode, error.errorMessage, error.message].join('|');
  }

  public add(error: LoaderError) {
    const key = this.buildKey(error);
    const existing = this.entries.get(key);
    if (existing) {
      existing.count += 1;
      return;
    }
    this.entries.set(key, { error, count: 1 });
  }

  public clear() {
    this.entries.clear();
  }

  public isEmpty() {
    return this.entries.size === 0;
  }

  public toReadableString() {
    if (this.entries.size === 0) {
      return 'Brak błędów.';
    }
    return Array.from(this.entries.values())
      .map(({ error, count }) => {
        const statusPart = error.statusCode ? `[${error.statusCode}] ` : '';
        const messagePart = error.message || 'Brak szczegółów';
        const errorMessagePart = error.errorMessage
          ? ` (${error.errorMessage})`
          : '';
        const countPart = count > 1 ? ` x${count}` : '';
        return `${statusPart}${messagePart}${errorMessagePart}${countPart}`;
      })
      .join('\n');
  }

  public toString() {
    return this.toReadableString();
  }
}
export class Loader {
  private static getAjaxErrorReason(
    errorThrown: string | undefined,
    textStatus: string | undefined,
    statusText: string | undefined,
  ) {
    return errorThrown || textStatus || statusText || 'nieznany błąd';
  }

  private static getAjaxErrorMessage(jqXHR: JQuery.jqXHR) {
    return jqXHR.responseJSON
      ? jqXHR.responseJSON.message
      : jqXHR.responseText || 'Brak odpowiedzi z serwera';
  }

  private static buildAjaxError(
    context: string,
    jqXHR: JQuery.jqXHR,
    textStatus?: string,
    errorThrown?: string,
  ): LoaderError {
    const errorReason = Loader.getAjaxErrorReason(
      errorThrown,
      textStatus,
      jqXHR.statusText,
    );
    return {
      errorMessage: `${context}${errorReason}`,
      statusCode: jqXHR.status,
      message: Loader.getAjaxErrorMessage(jqXHR),
    };
  }

  public static ErrorsStack = new LoaderErrorsStack();
  private static putHtmlToNode(
    idname: string,
    html: any,
    callback: any,
    data: any,
  ) {
    if (callback) {
      void $(idname).html(html).promise().done(callback(data));
    } else {
      $(idname).html(html);
    }
  }

  private static injectDataToTemplate(
    idname: string,
    template: string,
    data: any,
    callback: any,
    funOnResult = this.putHtmlToNode,
  ) {
    if (Array.isArray(data)) {
      data = { array: data };
    }
    const html = Mustache.render(template, data);
    funOnResult(idname, html, callback, data);
  }
  public static async getSyncJSONWithVersion(
    contentJson: string,
    data_param?: any,
    withToken?: boolean,
  ): Promise<any> {
    let error: LoaderError = {
      errorMessage: '',
      statusCode: 0,
      message: '',
    };
    try {
      let headers = {};
      if (withToken) {
        const activeToken = await getActiveToken();
        headers = {
          Authorization: 'Bearer ' + activeToken,
        };
      }
      DEBUG_MODE &&
        console.log('getSyncJSON', contentJson, data_param, headers);
      const result = await $.ajax({
        headers: headers,
        url: Loader.addVersionToUrl(contentJson),
        data: data_param,
        dataType: 'json',
      }).fail((jqXHR, textStatus, errorThrown) => {
        error = Loader.buildAjaxError(
          `Błąd podczas pobierania JSON [${contentJson}]!:`,
          jqXHR,
          textStatus,
          errorThrown,
        );
        //console.error(errorMessage);
      });
      return result;
    } catch (e) {
      console.error(error, e);
      throw new LoaderError(
        error.errorMessage,
        error.statusCode,
        error.message || 'Brak odpowiedzi z serwera',
      );
    }
  }

  public static async getSyncBlobWithVersion(
    contentBlob: string,
    data_param?: any,
    withToken?: boolean,
  ): Promise<any> {
    let error: LoaderError = {
      errorMessage: '',
      statusCode: 0,
      message: '',
    };
    try {
      let headers = {};
      if (withToken) {
        const activeToken = await getActiveToken();
        headers = {
          Authorization: 'Bearer ' + activeToken,
        };
      }
      DEBUG_MODE &&
        console.log('getSyncJSON', contentBlob, data_param, headers);
      const result = await $.ajax({
        headers: headers,
        url: Loader.addVersionToUrl(contentBlob),
        data: data_param,
        xhrFields: { responseType: 'blob' }, // dostaniemy Blob
      }).fail((jqXHR, textStatus, errorThrown) => {
        error = Loader.buildAjaxError(
          `Błąd podczas pobierania BLOB [${contentBlob}]!:`,
          jqXHR,
          textStatus,
          errorThrown,
        );
        //console.error(errorMessage);
      });
      return result;
    } catch (e) {
      console.error(error, e);
      throw new LoaderError(
        error.errorMessage,
        error.statusCode,
        error.message || 'Brak odpowiedzi z serwera',
      );
    }
  }

  private static getJSON(
    contentJson: string,
    data_param: any,
    callback: (json: any) => void,
  ) {
    void $.getJSON(contentJson, function (json) {
      if (callback) {
        callback(json);
      }
    }).fail(function (j, t, e) {
      //alert('BŁĄD JSON [' + contentJson + ']!:' + e);
      console.error('getJSON', contentJson, j, t, e);
    });
  }

  private static getDataFromParamContent(
    idname: string,
    template: string,
    contentJson: any,
    data_param: any,
    callback: any,
    funOnResult: any,
  ) {
    if (typeof contentJson === 'string') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      Loader.getJSON(contentJson, data_param, function (json: any) {
        Loader.injectDataToTemplate(
          idname,
          template,
          json,
          callback,
          funOnResult,
        );
      });
    } else {
      Loader.injectDataToTemplate(
        idname,
        template,
        contentJson,
        callback,
        funOnResult,
      );
    }
  }

  protected static addVersionToUrl(url: string) {
    if (url.includes('?')) {
      return url + '&ver=' + getBuildVersion();
    } else {
      // Jeśli nie, dodajemy pierwszy parametr
      return url + '?ver=' + getBuildVersion();
    }
  }

  public static loadContentWithVersion(
    idname: string,
    template_html: string,
    data_json?: any,
    callback?: any,
    funOnResult?: any,
  ) {
    template_html = Loader.addVersionToUrl(template_html); //dodanie wersjonowania templatów
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    $.get(
      template_html,
      function (template) {
        if (data_json) {
          Loader.getDataFromParamContent(
            idname,
            template,
            data_json,
            null,
            callback,
            funOnResult,
          );
        } else {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          $(idname).html(template).promise().done(callback);
        }
      },
      'html',
    ).fail(function (j, t, e) {
      console.error('loadContent', j, t, e);
      alert('BŁĄD WCZYTYWANIA TEMPLATE [' + template_html + ']!:' + e);
    });
  }

  public static loadCSS = function (href: string, id?: string) {
    $('#' + id).remove();
    const cssLink = $('<link>');
    $('head').append(cssLink); //IE hack: append before setting href

    cssLink.attr({
      id: id,
      rel: 'stylesheet',
      type: 'text/css',
      href: Loader.addVersionToUrl(href),
    });
  };

  public static loadJS = function (
    idname: string,
    scriptSrc: string,
    callback: () => void,
  ) {
    const scriptSrcVer = Loader.addVersionToUrl(scriptSrc);
    const findScript = $(`script[src="${scriptSrcVer}"]`);
    if (findScript.length) {
      DEBUG_MODE && console.log('Already loaded script:', scriptSrcVer);
      return callback();
    } else {
      DEBUG_MODE && console.log('Loading script:', scriptSrcVer);
      const script = document.createElement('script');
      script.src = scriptSrcVer;
      script.async = false;
      script.onload = callback;
      script.type = 'module';
      document.body.append(script);
    }
  };

  public static async post(url: string, param: any) {
    const activeToken = await getActiveToken();
    const headers = activeToken
      ? {
          Authorization: 'Bearer ' + activeToken,
        }
      : {};
    DEBUG_MODE && console.log('AJAX.POST', url, param, headers);
    return $.ajax({
      headers: headers,
      url: url,
      data: param,
      type: 'POST',
    });
  }

  public static async postSync(
    url: string,
    param: any,
    withToken = true,
    isFormData = false,
  ) {
    let error: LoaderError = {
      errorMessage: '',
      statusCode: 0,
      message: '',
    };
    try {
      let headers = {};
      if (withToken) {
        const activeToken = await getActiveToken();
        headers = {
          Authorization: 'Bearer ' + activeToken,
        };
      }

      // Bazowe opcje
      const ajaxOpts: JQueryAjaxSettings = {
        headers,
        url,
        data: param,
        method: 'POST',
      };

      if (isFormData) {
        /// obsługa upload plików itp.
        ajaxOpts.processData = false; // nie serializuj
        ajaxOpts.contentType = false; // boundary ustawi przeglądarka
      }

      const result = await $.ajax(ajaxOpts).fail(
        (jqXHR, textStatus, errorThrown) => {
          error = Loader.buildAjaxError(
            'Błąd podczas POST:',
            jqXHR,
            textStatus,
            errorThrown,
          );
        },
      );
      return result;
    } catch (e) {
      console.error(error, e);
      throw new LoaderError(
        error.errorMessage,
        error.statusCode,
        error.message || 'Brak odpowiedzi z serwera',
      );
    }
  }

  public static async postSyncWithRetry(
    url: string,
    param: any,
    withToken = true,
    isFormData = false,
    onError?: (error: unknown) => void,
    initialDelayMs = 1000,
    maxDelayMs = 30000,
  ) {
    if (param === null || typeof param === 'undefined') {
      throw new LoaderError(
        'Błąd podczas POST: brak danych',
        0,
        'Brak danych do wysłania',
      );
    }
    return Loader.retry(
      () => {
        return Loader.postSync(url, param, withToken, isFormData);
      },
      initialDelayMs,
      maxDelayMs,
      onError,
    );
  }

  public static async postJsonSync(
    url: string,
    payload: unknown,
    withToken = true,
  ) {
    let error: LoaderError = {
      errorMessage: '',
      statusCode: 0,
      message: '',
    };
    try {
      let headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (withToken) {
        const activeToken = await getActiveToken();
        headers = {
          ...headers,
          Authorization: 'Bearer ' + activeToken,
        };
      }

      const result = await $.ajax({
        headers,
        url,
        data: JSON.stringify(payload),
        method: 'POST',
        processData: false,
        contentType: 'application/json',
        dataType: 'json',
      }).fail((jqXHR, textStatus, errorThrown) => {
        error = Loader.buildAjaxError(
          'Błąd podczas POST:',
          jqXHR,
          textStatus,
          errorThrown,
        );
      });
      return result;
    } catch (e) {
      console.error(error, e);
      throw new LoaderError(
        error.errorMessage,
        error.statusCode,
        error.message || 'Brak odpowiedzi z serwera',
      );
    }
  }

  public static async patch(url: string, param: any) {
    let error: LoaderError = {
      errorMessage: '',
      statusCode: 0,
      message: '',
    };
    try {
      const activeToken = await getActiveToken();
      const headers = activeToken
        ? {
            Authorization: 'Bearer ' + activeToken,
          }
        : {};
      DEBUG_MODE && console.log('AJAX.PATCH', url, param, headers);
      const result = await $.ajax({
        headers: headers,
        url: url,
        data: param,
        //dataType: 'json',
        type: 'PATCH',
      }).fail((jqXHR, textStatus, errorThrown) => {
        error = Loader.buildAjaxError(
          'Błąd podczas PATCH:',
          jqXHR,
          textStatus,
          errorThrown,
        );
      });
      return result;
    } catch (e) {
      console.error(error, e);
      throw new LoaderError(
        error.errorMessage,
        error.statusCode,
        error.message || 'Brak odpowiedzi z serwera',
      );
    }
  }

  public static async patchWithRetry(
    url: string,
    param: any,
    onError?: (error: unknown) => void,
    initialDelayMs = 1000,
    maxDelayMs = 30000,
  ) {
    return Loader.retry(
      () => Loader.patch(url, param),
      initialDelayMs,
      maxDelayMs,
      onError,
    );
  }

  protected static async retry<T>(
    attempt: () => Promise<T>,
    initialDelayMs: number,
    maxDelayMs: number,
    onError?: (error: unknown) => void,
  ): Promise<T> {
    const sleep = (ms: number) =>
      new Promise<void>(resolve => window.setTimeout(resolve, ms));
    let delayMs = initialDelayMs;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        return await attempt();
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
        onError?.(e);
        await sleep(delayMs);
        delayMs = Math.min(delayMs * 2, maxDelayMs);
      }
    }
  }
  public static getWithVersion(url: string) {
    return $.get(Loader.addVersionToUrl(url));
  }
}
